import { useState, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { Spin, Tag, Tooltip, Tabs, Button, Modal, Select, message } from "antd";
import { ChevronLeft, ChevronRight, Calendar, MapPin, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useJobList, useJobUpdate } from "../../../hooks/api/jobs/hooks";
import { IJob } from "../../../types/api/job";
import { useTerm } from "../../../lib/terminology";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";
import InitialsAvatar from "../../../components/ui/InitialsAvatar";

dayjs.extend(isoWeek);

const STATUS_COLOR: Record<string, string> = {
  draft: "default",
  scheduled: "blue",
  in_progress: "orange",
  completed: "green",
  cancelled: "red",
};

const STATUS_BG: Record<string, string> = {
  draft: "bg-slate-100 border-slate-300 text-slate-700",
  scheduled: "bg-blue-50 border-blue-300 text-blue-800",
  in_progress: "bg-amber-50 border-amber-300 text-amber-800",
  completed: "bg-emerald-50 border-emerald-300 text-emerald-700",
  cancelled: "bg-red-50 border-red-200 text-red-600 line-through opacity-60",
};

const STATUS_DOT_COLOR: Record<string, string> = {
  draft: "bg-slate-400",
  scheduled: "bg-blue-500",
  in_progress: "bg-amber-500",
  completed: "bg-emerald-500",
  cancelled: "bg-red-400",
};

const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

function JobCard({ job }: { job: IJob }) {
  const navigate = useNavigate();
  return (
    <Tooltip
      title={
        <div className="text-xs">
          <div className="font-semibold">{job.title}</div>
          {job.client_name && <div>Client: {job.client_name}</div>}
          {job.assigned_user_name && <div>Assigned: {job.assigned_user_name}</div>}
          {job.scheduled_start && <div>Start: {dayjs(job.scheduled_start).format("HH:mm")}</div>}
          {job.scheduled_end && <div>End: {dayjs(job.scheduled_end).format("HH:mm")}</div>}
          <div>Status: {job.status.replace(/_/g, " ")}</div>
          <div className="mt-0.5 italic opacity-75">Click to open job</div>
        </div>
      }
      placement="top"
    >
      <div
        className={`text-[11px] rounded border px-2 py-1 mb-1 cursor-pointer leading-tight hover:brightness-95 transition-all ${STATUS_BG[job.status] ?? "bg-slate-50 border-slate-200"}`}
        onClick={() => navigate(`/jobs?jobId=${job.id}`)}
      >
        <div className="font-semibold truncate max-w-[120px]">{job.title}</div>
        {job.client_name && <div className="text-[10px] truncate opacity-75">{job.client_name}</div>}
        <div className="text-[10px] opacity-75">
          {job.assigned_user_name ? job.assigned_user_name : <span className="italic">Unassigned</span>}
        </div>
        {job.scheduled_start && (
          <div className="text-[10px] opacity-75">{dayjs(job.scheduled_start).format("HH:mm")}</div>
        )}
      </div>
    </Tooltip>
  );
}

// ── Reassign Modal ─────────────────────────────────────────────────────────────
function ReassignModal({
  open,
  onClose,
  workerJobs,
  allWorkers,
}: {
  open: boolean;
  onClose: () => void;
  workerJobs: IJob[];
  allWorkers: { id: number; first_name: string; last_name: string; name?: string }[];
}) {
  const [msgApi, ctx] = message.useMessage();
  const [selectedJobId, setSelectedJobId] = useState<number | undefined>();
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | undefined>();

  const updateJob = useJobUpdate({
    onSuccess: () => {
      msgApi.success("Job reassigned");
      setSelectedJobId(undefined);
      setSelectedWorkerId(undefined);
      onClose();
    },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Error reassigning job"),
  });

  const handleConfirm = () => {
    if (!selectedJobId || !selectedWorkerId) return;
    const job = workerJobs.find((j) => j.id === selectedJobId);
    if (!job) return;
    updateJob.mutate({ id: selectedJobId, assigned_user_id: selectedWorkerId });
  };

  const handleCancel = () => {
    setSelectedJobId(undefined);
    setSelectedWorkerId(undefined);
    onClose();
  };

  return (
    <>
      {ctx}
      <Modal
        open={open}
        title="Reassign Job"
        onCancel={handleCancel}
        onOk={handleConfirm}
        okText="Confirm"
        confirmLoading={updateJob.isPending}
        okButtonProps={{ disabled: !selectedJobId || !selectedWorkerId }}
      >
        <div className="flex flex-col gap-4 py-2">
          <div>
            <div className="text-xs text-slate-500 mb-1">Job to reassign</div>
            <Select
              style={{ width: "100%" }}
              placeholder="Select job"
              value={selectedJobId}
              onChange={setSelectedJobId}
            >
              {workerJobs.map((j) => (
                <Select.Option key={j.id} value={j.id}>
                  {j.title}
                  {j.scheduled_start ? ` — ${dayjs(j.scheduled_start).format("HH:mm")}` : ""}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Assign to</div>
            <Select
              style={{ width: "100%" }}
              placeholder="Select worker"
              value={selectedWorkerId}
              onChange={setSelectedWorkerId}
              showSearch
              optionFilterProp="children"
            >
              {allWorkers.map((w) => (
                <Select.Option key={w.id} value={w.id}>
                  {w.name || `${w.first_name} ${w.last_name}`}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ── Engineer Cards View ────────────────────────────────────────────────────────
function EngineerCardsView() {
  const today = dayjs().format("YYYY-MM-DD");
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");

  const { data: jobsRes, isLoading: jobsLoading } = useJobList({
    page: 1,
    limit: 500,
    scheduled_start_from: today,
    scheduled_start_to: tomorrow,
  });
  const todayJobs: IJob[] = (jobsRes?.data ?? []) as IJob[];

  const { data: workersRes, isLoading: workersLoading } = useFieldWorkerList({ page: 1, limit: 200, status: "ACTIVE" });
  const allWorkers: any[] = (workersRes as any)?.data ?? [];

  const [reassignTarget, setReassignTarget] = useState<{ jobs: IJob[]; workerName: string } | null>(null);

  const jobsByWorker = useMemo(() => {
    const map: Record<number, IJob[]> = {};
    for (const job of todayJobs) {
      if (job.assigned_user_id) {
        if (!map[job.assigned_user_id]) map[job.assigned_user_id] = [];
        map[job.assigned_user_id].push(job);
      }
    }
    return map;
  }, [todayJobs]);

  const unassignedJobs = todayJobs.filter((j) => !j.assigned_user_id);

  const isLoading = jobsLoading || workersLoading;

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spin size="large" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <Users size={14} />
          {dayjs().format("dddd, D MMMM YYYY")} — today&apos;s engineer assignments
        </div>
        <div className="text-xs text-slate-400">
          {todayJobs.length} job(s) scheduled today
        </div>
      </div>

      {allWorkers.length === 0 && todayJobs.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Users size={32} className="mx-auto mb-2 opacity-30" />
          <div className="text-sm">No engineers or jobs found for today.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Engineers with jobs today */}
          {allWorkers.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {allWorkers.map((w) => {
                const workerJobs = jobsByWorker[w.id] ?? [];
                const workerName = w.name || `${w.first_name} ${w.last_name}`;
                return (
                  <div
                    key={w.id}
                    className="bg-white rounded-xl border border-slate-100 shadow-sm p-4"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <InitialsAvatar
                        name={workerName}
                        src={w.profile_image}
                        size={36}
                        className="!rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-800 truncate">{workerName}</div>
                        <div className="text-xs text-slate-400">
                          {workerJobs.length === 0
                            ? "No jobs today"
                            : `${workerJobs.length} job${workerJobs.length !== 1 ? "s" : ""} today`}
                        </div>
                      </div>
                    </div>

                    {workerJobs.length > 0 && (
                      <div className="flex flex-col gap-1 mb-2">
                        {workerJobs.map((job) => (
                          <div key={job.id} className="flex items-center justify-between gap-1">
                            <div className="flex-1 min-w-0">
                              <JobCard job={job} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {workerJobs.length > 0 && (
                      <Button
                        size="small"
                        className="w-full mt-1"
                        onClick={() => setReassignTarget({ jobs: workerJobs, workerName })}
                      >
                        Reassign
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Unassigned jobs */}
          {unassignedJobs.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
              <div className="px-5 py-3 border-b border-slate-100">
                <h2 className="text-sm font-semibold text-slate-700">Unassigned Jobs Today</h2>
                <p className="text-xs text-slate-400">{unassignedJobs.length} job(s) with no engineer</p>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {unassignedJobs.map((job) => (
                  <div
                    key={job.id}
                    className={`rounded-lg border px-3 py-2 text-sm ${STATUS_BG[job.status] ?? "bg-slate-50 border-slate-200"}`}
                  >
                    <div className="font-semibold text-sm truncate">{job.title}</div>
                    {job.client_name && (
                      <div className="text-xs opacity-75 truncate">{job.client_name}</div>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] opacity-75 italic">Unassigned</span>
                      <Tag color={STATUS_COLOR[job.status]} className="text-[10px] m-0">
                        {job.status.replace(/_/g, " ")}
                      </Tag>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {reassignTarget && (
        <ReassignModal
          open={!!reassignTarget}
          onClose={() => setReassignTarget(null)}
          workerJobs={reassignTarget.jobs}
          allWorkers={allWorkers}
        />
      )}
    </div>
  );
}

// ── Calendar View ──────────────────────────────────────────────────────────────
function CalendarView() {
  const term = useTerm();
  const navigate = useNavigate();

  const [view, setView] = useState<"day" | "week" | "month">("week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [currentDay, setCurrentDay] = useState<Dayjs>(dayjs());
  const [monthDate, setMonthDate] = useState<Dayjs>(dayjs().startOf("month"));

  const fetchRange = useMemo(() => {
    if (view === "day") {
      return {
        from: currentDay.format("YYYY-MM-DD"),
        to: currentDay.add(1, "day").format("YYYY-MM-DD"),
      };
    }
    if (view === "month") {
      const start = monthDate.startOf("month").startOf("isoWeek" as any);
      const end = monthDate.endOf("month").endOf("isoWeek" as any);
      return {
        from: start.format("YYYY-MM-DD"),
        to: end.add(1, "day").format("YYYY-MM-DD"),
      };
    }
    const monday = dayjs().add(weekOffset, "week").startOf("isoWeek" as any);
    return {
      from: monday.format("YYYY-MM-DD"),
      to: monday.add(6, "day").add(1, "day").format("YYYY-MM-DD"),
    };
  }, [view, weekOffset, currentDay, monthDate]);

  const { data: jobsRes, isLoading } = useJobList({
    page: 1,
    limit: 500,
    scheduled_start_from: fetchRange.from,
    scheduled_start_to: fetchRange.to,
  });
  const allJobs: IJob[] = (jobsRes?.data ?? []) as IJob[];

  const days = useMemo(() => {
    const monday = dayjs().add(weekOffset, "week").startOf("isoWeek" as any);
    return Array.from({ length: 7 }, (_, i) => monday.add(i, "day"));
  }, [weekOffset]);

  const weekStart = days[0];
  const weekEnd = days[6];

  const weekJobs = allJobs.filter((j) => {
    if (!j.scheduled_start) return false;
    const d = dayjs(j.scheduled_start);
    return d.isAfter(weekStart.subtract(1, "day")) && d.isBefore(weekEnd.add(1, "day"));
  });

  const jobsByDate = useMemo(() => {
    const map: Record<string, IJob[]> = {};
    for (const day of days) {
      const key = day.format("YYYY-MM-DD");
      map[key] = weekJobs.filter((j) => dayjs(j.scheduled_start!).format("YYYY-MM-DD") === key);
    }
    return map;
  }, [weekJobs, days]);

  const unscheduledJobs = allJobs.filter(
    (j) => !j.scheduled_start && j.status !== "completed" && j.status !== "cancelled"
  );

  const today = dayjs().format("YYYY-MM-DD");

  const onTimeSlotClick = (date: Dayjs, hour: number) => {
    const start = date.hour(hour).minute(0).second(0).millisecond(0);
    const end = start.add(1, "hour");
    navigate(`/jobs?create=1&start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`);
  };

  const goPrev = () => {
    if (view === "day") setCurrentDay((d) => d.subtract(1, "day"));
    else if (view === "week") setWeekOffset((w) => w - 1);
    else setMonthDate((m) => m.subtract(1, "month"));
  };
  const goNext = () => {
    if (view === "day") setCurrentDay((d) => d.add(1, "day"));
    else if (view === "week") setWeekOffset((w) => w + 1);
    else setMonthDate((m) => m.add(1, "month"));
  };
  const goToday = () => {
    setCurrentDay(dayjs());
    setWeekOffset(0);
    setMonthDate(dayjs().startOf("month"));
  };
  const isTodayActive =
    view === "day"
      ? currentDay.format("YYYY-MM-DD") === today
      : view === "week"
      ? weekOffset === 0
      : monthDate.format("YYYY-MM") === dayjs().format("YYYY-MM");

  const dateLabel = useMemo(() => {
    if (view === "day") return currentDay.format("dddd, D MMM YYYY");
    if (view === "month") return monthDate.format("MMMM YYYY");
    return `${days[0].format("D MMM")} – ${days[6].format("D MMM YYYY")}`;
  }, [view, currentDay, monthDate, days]);

  const dayJobs = useMemo(() => {
    const key = currentDay.format("YYYY-MM-DD");
    return allJobs
      .filter((j) => j.scheduled_start && dayjs(j.scheduled_start).format("YYYY-MM-DD") === key)
      .sort((a, b) => dayjs(a.scheduled_start!).diff(dayjs(b.scheduled_start!)));
  }, [allJobs, currentDay]);

  const monthCells = useMemo(() => {
    const firstOfMonth = monthDate.startOf("month");
    const startOffset = firstOfMonth.isoWeekday() - 1;
    const daysInMonth = monthDate.daysInMonth();
    const gridStart = firstOfMonth.subtract(startOffset, "day");
    const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
    return Array.from({ length: totalCells }, (_, i) => gridStart.add(i, "day"));
  }, [monthDate]);

  const jobsByDateMap = useMemo(() => {
    const map: Record<string, IJob[]> = {};
    for (const job of allJobs) {
      if (!job.scheduled_start) continue;
      const key = dayjs(job.scheduled_start).format("YYYY-MM-DD");
      if (!map[key]) map[key] = [];
      map[key].push(job);
    }
    return map;
  }, [allJobs]);

  const HOURS = Array.from({ length: 24 }, (_, i) => i);

  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-medium mr-2">
          {(["day", "week", "month"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 capitalize transition-colors ${
                view === v
                  ? "bg-primary text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        <span className="text-sm text-slate-500 flex-1 truncate">{dateLabel}</span>

        <button
          onClick={goPrev}
          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          onClick={goToday}
          disabled={isTodayActive}
          className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
            isTodayActive
              ? "border-slate-100 text-slate-300 cursor-default bg-white"
              : "border-slate-200 hover:bg-slate-50 text-slate-600"
          }`}
        >
          Today
        </button>
        <button
          onClick={goNext}
          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {view === "week" && (
            <>
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden mb-4">
                <div className="overflow-x-auto">
                  <div style={{ minWidth: 700 }}>
                    <div className="grid grid-cols-7 border-b border-slate-100">
                      {days.map((d, i) => {
                        const key = d.format("YYYY-MM-DD");
                        const isToday = key === today;
                        return (
                          <div
                            key={key}
                            className={`py-3 text-center border-r border-slate-50 last:border-r-0 ${isToday ? "bg-slate-100" : ""}`}
                          >
                            <div
                              className={`text-[11px] font-medium uppercase tracking-wide ${isToday ? "text-primary" : "text-slate-400"}`}
                            >
                              {DAY_LABELS[i]}
                            </div>
                            <div
                              className={`text-lg font-bold mt-0.5 ${isToday ? "text-primary" : "text-slate-700"}`}
                            >
                              {d.format("D")}
                            </div>
                            <div className="text-[10px] text-slate-400">{d.format("MMM")}</div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-7 min-h-48">
                      {days.map((d) => {
                        const key = d.format("YYYY-MM-DD");
                        const colJobs = jobsByDate[key] ?? [];
                        const isToday = key === today;
                        return (
                          <div
                            key={key}
                            className={`p-2 border-r border-slate-50 last:border-r-0 align-top cursor-pointer ${isToday ? "bg-slate-50/50" : "hover:bg-slate-50/30"}`}
                            title="Click to create a job at 09:00"
                            onClick={(e) => {
                              if (e.target === e.currentTarget) {
                                onTimeSlotClick(d, 9);
                              }
                            }}
                          >
                            {colJobs.length === 0 ? (
                              <div
                                className="text-[10px] text-slate-300 text-center mt-4 select-none"
                                onClick={() => onTimeSlotClick(d, 9)}
                              >
                                + add
                              </div>
                            ) : (
                              colJobs.map((job) => <JobCard key={job.id} job={job} />)
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mb-6">
                {Object.entries(STATUS_COLOR).map(([status, color]) => (
                  <div key={status} className="flex items-center gap-1.5">
                    <Tag color={color} className="text-[10px] m-0">
                      {status.replace(/_/g, " ")}
                    </Tag>
                  </div>
                ))}
              </div>

              {unscheduledJobs.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-slate-700">
                        Unscheduled {term("job", true)}
                      </h2>
                      <p className="text-xs text-slate-400">
                        {unscheduledJobs.length} active{" "}
                        {term("job", unscheduledJobs.length !== 1).toLowerCase()} with no scheduled
                        date
                      </p>
                    </div>
                  </div>
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {unscheduledJobs.map((job) => (
                      <div
                        key={job.id}
                        className={`rounded-lg border px-3 py-2 text-sm ${STATUS_BG[job.status] ?? "bg-slate-50 border-slate-200"}`}
                      >
                        <div className="font-semibold text-sm truncate">{job.title}</div>
                        {job.client_name && (
                          <div className="text-xs opacity-75 truncate">{job.client_name}</div>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] opacity-75">
                            {job.assigned_user_name ?? (
                              <span className="italic">Unassigned</span>
                            )}
                          </span>
                          <Tag color={STATUS_COLOR[job.status]} className="text-[10px] m-0">
                            {job.status.replace(/_/g, " ")}
                          </Tag>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {view === "day" && (
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">
                  {currentDay.format("dddd, D MMMM YYYY")}
                </span>
                <span className="text-xs text-slate-400">
                  {dayJobs.length} {term("job", dayJobs.length !== 1).toLowerCase()}
                </span>
              </div>

              <div className="overflow-y-auto" style={{ maxHeight: 560 }}>
                {HOURS.map((hour) => {
                  const hourJobs = dayJobs.filter(
                    (j) => dayjs(j.scheduled_start!).hour() === hour
                  );
                  const isCurrentHour =
                    currentDay.format("YYYY-MM-DD") === today &&
                    dayjs().hour() === hour;
                  return (
                    <div
                      key={hour}
                      className={`flex border-b border-slate-50 last:border-b-0 min-h-[48px] group cursor-pointer hover:bg-blue-50/30 transition-colors ${isCurrentHour ? "bg-blue-50/20" : ""}`}
                      title={`Create job at ${String(hour).padStart(2, "0")}:00`}
                      onClick={() => onTimeSlotClick(currentDay, hour)}
                    >
                      <div className="w-14 flex-shrink-0 text-right pr-3 pt-2 text-[11px] text-slate-400 font-mono select-none">
                        {String(hour).padStart(2, "0")}:00
                      </div>
                      <div className="flex-1 p-1.5 flex flex-col gap-1">
                        {hourJobs.length > 0 ? (
                          hourJobs.map((job) => (
                            <div
                              key={job.id}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <JobCard job={job} />
                            </div>
                          ))
                        ) : (
                          <div className="text-[10px] text-slate-200 opacity-0 group-hover:opacity-100 transition-opacity select-none mt-1 ml-1">
                            + click to create
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === "month" && (
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden mb-4">
              <div className="grid grid-cols-7 border-b border-slate-100">
                {DAY_LABELS.map((label) => (
                  <div
                    key={label}
                    className="py-2 text-center text-[11px] font-medium uppercase tracking-wide text-slate-400"
                  >
                    {label}
                  </div>
                ))}
              </div>

              <div
                className="grid grid-cols-7"
                style={{ gridAutoRows: "minmax(80px, auto)" }}
              >
                {monthCells.map((cell) => {
                  const key = cell.format("YYYY-MM-DD");
                  const isThisMonth = cell.month() === monthDate.month();
                  const isToday = key === today;
                  const cellJobs = jobsByDateMap[key] ?? [];
                  const visibleJobs = cellJobs.slice(0, 3);
                  const extraCount = cellJobs.length - visibleJobs.length;

                  return (
                    <div
                      key={key}
                      className={`border-r border-b border-slate-50 last:border-r-0 p-1.5 cursor-pointer transition-colors ${
                        isThisMonth ? "bg-white hover:bg-slate-50/60" : "bg-slate-50/40 hover:bg-slate-100/40"
                      } ${isToday ? "ring-1 ring-inset ring-primary/30" : ""}`}
                      title={`View ${cell.format("D MMM")} or create a job`}
                      onClick={() => {
                        setCurrentDay(cell);
                        setView("day");
                      }}
                    >
                      <div
                        className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                          isToday
                            ? "bg-primary text-white"
                            : isThisMonth
                            ? "text-slate-700"
                            : "text-slate-300"
                        }`}
                      >
                        {cell.format("D")}
                      </div>

                      <div className="flex flex-col gap-0.5">
                        {visibleJobs.map((job) => (
                          <div
                            key={job.id}
                            className="flex items-center gap-1 text-[10px] leading-tight"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/jobs?jobId=${job.id}`);
                            }}
                            title={job.title}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT_COLOR[job.status] ?? "bg-slate-400"}`}
                            />
                            <span className="truncate text-slate-600">{job.title}</span>
                          </div>
                        ))}
                        {extraCount > 0 && (
                          <div className="text-[10px] text-primary font-medium mt-0.5">
                            +{extraCount} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view !== "week" && (
            <div className="flex flex-wrap gap-3 mb-4">
              {Object.entries(STATUS_COLOR).map(([status, color]) => (
                <div key={status} className="flex items-center gap-1.5">
                  <Tag color={color} className="text-[10px] m-0">
                    {status.replace(/_/g, " ")}
                  </Tag>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
const SchedulePage = () => {
  const term = useTerm();

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            Schedule
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            View scheduled jobs on the calendar and track engineer assignments
          </p>
        </div>
      </div>

      <Tabs
        defaultActiveKey="calendar"
        items={[
          {
            key: "calendar",
            label: (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                Calendar
              </span>
            ),
            children: <CalendarView />,
          },
          {
            key: "engineers",
            label: (
              <span className="flex items-center gap-1.5">
                <MapPin size={14} />
                Engineers Today
              </span>
            ),
            children: <EngineerCardsView />,
          },
        ]}
      />
    </div>
  );
};

export default SchedulePage;
